/**
 * SectionThemeTagger — v3.18.2
 *
 * Side-panel widget for the Proposal Workspace: shows every Win Theme
 * (T1, T2, T3...) from the v3.18.0 Registry and lets the author toggle
 * which themes the current section threads. Tags are persisted via
 * `PUT /api/pursuits/{id}/strategy/theme-tags/{section_id}` and feed the
 * Theme Coverage Heatmap on the Strategy page.
 *
 * Designed to live inside the dark-themed proposal editor sidebar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Zap, Ghost, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SectionThemeTagger({ pursuitId, sectionId }) {
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(true);
  const [themes, setThemes] = useState([]);
  const [tagged, setTagged] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchThemes = useCallback(async () => {
    if (!pursuitId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/win-themes`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        const body = await res.json();
        setThemes(body.themes || []);
      }
    } catch (_) { /* silent */ }
  }, [pursuitId, getAuthHeaders]);

  const fetchTags = useCallback(async () => {
    if (!pursuitId || !sectionId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/theme-tags/${encodeURIComponent(sectionId)}`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        const body = await res.json();
        setTagged(body.themes_tagged || []);
      }
    } catch (_) { /* silent */ }
  }, [pursuitId, sectionId, getAuthHeaders]);

  useEffect(() => { fetchThemes(); }, [fetchThemes]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  const toggle = async (themeId) => {
    const next = tagged.includes(themeId)
      ? tagged.filter(t => t !== themeId)
      : [...tagged, themeId];
    setTagged(next);
    setSaving(true);
    try {
      await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/theme-tags/${encodeURIComponent(sectionId)}`,
        {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ themes_tagged: next }),
        },
      );
    } catch (_) { /* silent — UI optimistic */ } finally {
      setSaving(false);
    }
  };

  const taggedCount = tagged.length;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-testid="section-theme-tagger"
    >
      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between border-b border-slate-800 hover:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-400" />
          <span className="font-medium text-sm">Themes Threading</span>
        </div>
        <Badge
          variant="outline"
          className={cn(
            'text-xs border-slate-700',
            taggedCount > 0 ? 'bg-violet-900/40 text-violet-200 border-violet-700' : '',
          )}
          data-testid="section-theme-tagger-count"
        >
          {taggedCount}/{themes.length || '—'}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-3 space-y-2 border-b border-slate-800 max-h-72 overflow-y-auto">
        {themes.length === 0 ? (
          <div className="text-center py-4" data-testid="section-theme-tagger-empty">
            <Sparkles className="w-6 h-6 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500 leading-snug">
              No themes captured yet. Open the <span className="font-semibold text-violet-300">Strategy</span> page to define T1, T2…
            </p>
          </div>
        ) : (
          <>
            <p className="text-[11px] text-slate-500 mb-2">
              Click a theme to mark it as threaded through this section.
            </p>
            {themes.map((t) => {
              const isOn = tagged.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  disabled={saving}
                  data-testid={`section-theme-toggle-${t.code}`}
                  className={cn(
                    'w-full text-left p-2 rounded-md border transition-colors flex items-start gap-2',
                    isOn
                      ? 'bg-violet-900/30 border-violet-600/60 hover:bg-violet-900/40'
                      : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800/70',
                  )}
                >
                  <div
                    className={cn(
                      'flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center mt-0.5',
                      isOn ? 'bg-violet-500 border-violet-400' : 'border-slate-600',
                    )}
                  >
                    {isOn && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono text-[11px] font-bold text-violet-300">{t.code}</span>
                      <span className="text-xs text-slate-200 font-medium truncate">{t.title}</span>
                      {t.discriminator && (
                        <Zap className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      )}
                      {t.ghost && (
                        <Ghost className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      )}
                    </div>
                    {t.body && (
                      <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{t.body}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
